import React from 'react';

// Import local images from the assets/timeline folder
import imgTravelerDiscovers from '/assets/timeline/Travel-Rethinkways-Timeline.avif';
import imgTravelWebStory from '/assets/timeline/Travel-Rethinkways-Timeline-2.avif';
import imgExploresSuperDeal from '/assets/timeline/Travel-Rethinkways-Timeline-3.avif';
import imgFeaturedBrand from '/assets/timeline/Travel-Rethinkways-Timeline-5.avif';
import imgDiscoversPackage from '/assets/timeline/Travel-Rethinkways-Timeline-4.avif';
import imgEngagesSocially from '/assets/timeline/Travel-Rethinkways-Timeline-6.avif';
import imgTravelExperience from '/assets/timeline/Travel-Rethinkways-Timeline-7.avif';

const TravelerFlowSection = () => {
    const flowSteps = [
        {
            label: "SPARK",
            text: "Travel",
            text1: "Inspiration",
            description: "Exploring immersive stories, guides, and destination experiences.",
            image: imgTravelerDiscovers
        },
        {
            label: "EXPLORE",
            text: "Immersive",
            text1: "Web Stories",
            description: "Experiencing destinations visually through visual storytelling formats.",
            image: imgTravelWebStory
        },
        {
            label: "DISCOVER",
            text: "Featured",
            text1: "Smart Deals",
            description: "Exploring relevant offers, experiences, and travel opportunities.",
            image: imgExploresSuperDeal
        },
        {
            label: "CONNECT",
            text: "Trusted",
            text1: "Travel Brands",
            description: "Connecting with brands naturally during the travel discovery journey.",
            image: imgFeaturedBrand
        },
        {
            label: "PLAN",
            text: "Ideas &",
            text1: "Itineraries",
            description: "Evaluating stays, sightseeings, and curated travel experiences.",
            image: imgDiscoversPackage
        },
        {
            label: "ENGAGE",
            text: "Interacting",
            text1: "Community",
            description: "Interacting with storytelling, recommendations, and destination inspiration.",
            image: imgEngagesSocially
        },
        {
            label: "EXPERIENCE",
            text: "Meaningful",
            text1: "Real Stories",
            description: "Moving from inspiration and discovery toward real travel decisions.",
            image: imgTravelExperience
        }
    ];

    return (
        <section className="w-full bg-[#000000] py-10 sm:py-20 md:py-20 font-sans overflow-hidden">
            <div className="max-w-[1200px] mx-auto px-4 sm:px-8">

                {/* Section Header */}
                <div className="w-full flex flex-col items-center justify-center text-center mx-auto mb-16 lg:mb-24 relative z-30">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#d02525] border border-gray-800 mb-5 sm:mb-6 shadow-sm">
                        <span className="text-white font-poppins text-[10px] sm:text-xs md:text-sm font-semibold tracking-widest uppercase">
                            Multi-Touch Travel Influence
                        </span>
                    </div>

                    <h2 className="text-[24px] sm:text-4xl md:text-5xl lg:text-[44px] xl:text-[54px] font-medium font-black text-white tracking-tight mb-3 sm:mb-2 leading-[1.2] lg:leading-tight max-w-5xl mx-auto">
                        Travel Decisions Happen Across
                    </h2>

                    <p className="text-gray-300 font-poppins text-[18px] sm:text-base md:text-xl lg:text-xl xl:text-2xl leading-relaxed max-w-[95%] sm:max-w-6xl mx-auto mb-0 sm:mb-5 mt-0 sm:mt-1">
                        The travel ecosystem’s connected touchpoints help brands stay visible
                        <br className="hidden md:block" />
                        {" "}throughout the modern traveler journey from inspiration to booking.
                    </p>
                </div>

                {/* Vertical Timeline Container */}
                <div className="relative w-full py-0 mt-8 md:mt-0">
                    <div className="flex flex-col gap-16 sm:gap-20 md:gap-12 lg:gap-16">
                        {flowSteps.map((step, index) => {
                            const isEven = index % 2 === 0;

                            return (
                                <div key={index} className="relative flex flex-col md:flex-row items-center justify-between w-full group gap-6 md:gap-0">

                                    {/* --- MATHEMATICALLY PERFECT LINE SEGMENTS --- */}
                                    {index !== flowSteps.length - 1 && (
                                        <div className="absolute left-6 md:left-1/2 top-1/2 -bottom-16 sm:-bottom-20 md:-bottom-12 lg:-bottom-16 w-[2px] border-l-[2px] border-dashed border-[#2A3036] -translate-x-1/2 z-0 pointer-events-none"></div>
                                    )}

                                    {index !== 0 && (
                                        <div className="absolute left-6 md:left-1/2 top-0 bottom-1/2 w-[2px] border-l-[2px] border-dashed border-[#2A3036] -translate-x-1/2 z-0 pointer-events-none"></div>
                                    )}

                                    {/* Timeline Marker (The Dot) */}
                                    <div className="absolute left-6 md:left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-black border-[3px] border-[#D02525] z-20 flex items-center justify-center shadow-[0_0_10px_rgba(208,37,37,0.5)] transition-all duration-300 group-hover:scale-150 group-hover:border-[#D02525] group-hover:shadow-[0_0_20px_rgba(208,37,37,0.8)]">
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#D02525]"></div>
                                    </div>

                                    {/* ========================================================= */}
                                    {/* 1. MOBILE ONLY: COMBINED IMAGE & TEXT CARD (< md)         */}
                                    {/* ========================================================= */}
                                    <div className="md:hidden relative w-[calc(100%-3.5rem)] sm:w-[calc(100%-4rem)] ml-14 sm:ml-16 rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.5)] border border-gray-800 min-h-[420px] sm:min-h-[480px] flex flex-col justify-end group">
                                        {/* Background Image - Zoom hover removed here */}
                                        <img src={step.image} alt={step.text} className="absolute inset-0 w-full h-full object-cover z-0" />

                                        {/* Dark Gradient Overlay for Text Readability */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-[#151618]/80 to-transparent z-10"></div>

                                        {/* Text Content */}
                                        <div className="relative z-20 p-6 sm:p-8 pt-32">
                                            <div>
                                                <div className="inline-block bg-[#d02525] border border-gray-700 text-white text-xs sm:text-sm font-bold px-4 py-2 rounded-md mb-4 uppercase tracking-wider">
                                                    {step.label}
                                                </div>
                                            </div>
                                            <h3 className="text-white text-3xl sm:text-4xl whitespace-normal break-words mb-3 sm:mb-4 font-poppins tracking-[1px] leading-[1.2]">
                                                {step.text1 ? (
                                                    <>
                                                        <span className="block font-thin">{step.text}</span>
                                                        <span className="block font-black">{step.text1}</span>
                                                    </>
                                                ) : (
                                                    <span className="font-black">{step.text}</span>
                                                )}
                                            </h3>
                                            <p className="text-gray-300 text-[16px] sm:text-lg font-poppins leading-[1.6]">
                                                {step.description}
                                            </p>
                                        </div>
                                    </div>


                                    {/* ========================================================= */}
                                    {/* 2. DESKTOP ONLY: LEFT SIDE BLOCK (md and up)              */}
                                    {/* ========================================================= */}
                                    <div className={`hidden md:flex md:w-[45%] items-center justify-end`}>
                                        {isEven ? (
                                            /* Text Card */
                                            <div className="w-full md:aspect-[4/3] flex flex-col justify-start bg-[#151618] border border-gray-800 rounded-2xl p-6 lg:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.5)] transition-colors duration-300 hover:bg-[#151617] text-left">
                                                <div>
                                                    <div className="inline-block bg-[#d02525] border border-gray-700 text-white text-sm font-bold px-4 py-2 rounded-md mb-4 uppercase tracking-wider">
                                                        {step.label}
                                                    </div>
                                                </div>
                                                <h3 className="text-gray-200 md:text-3xl lg:text-[50px] xl:text-[65px] whitespace-normal break-words mb-4 sm:mb-5 font-poppins tracking-[1px] md:leading-[1.1] lg:leading-[78px]">
                                                    {step.text1 ? (
                                                        <>
                                                            <span className="block font-thin">{step.text}</span>
                                                            <span className="block font-black">{step.text1}</span>
                                                        </>
                                                    ) : (
                                                        <span className="font-black">{step.text}</span>
                                                    )}
                                                </h3>
                                                <p className="text-gray-400 md:text-lg lg:text-xl xl:text-2xl font-poppins md:leading-[1.5] lg:leading-[33px]">
                                                    {step.description}
                                                </p>
                                            </div>
                                        ) : (
                                            /* Image */
                                            <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-lg border border-gray-800 transition-all duration-500 hover:border-gray-600">
                                                <img src={step.image} alt={step.text} className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                    </div>


                                    {/* ========================================================= */}
                                    {/* 3. DESKTOP ONLY: RIGHT SIDE BLOCK (md and up)             */}
                                    {/* ========================================================= */}
                                    <div className={`hidden md:flex md:w-[45%] items-center justify-start`}>
                                        {!isEven ? (
                                            /* Text Card */
                                            <div className="w-full md:aspect-[4/3] flex flex-col justify-start bg-[#151618] border border-gray-800 rounded-2xl p-6 lg:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.5)] transition-colors duration-300 hover:bg-[#151617] text-left font-poppins">
                                                <div>
                                                    <div className="inline-block bg-[#d02525] border border-gray-700 text-white text-sm font-bold px-4 py-2 rounded-md mb-4 uppercase tracking-wider">
                                                        {step.label}
                                                    </div>
                                                </div>
                                                <h3 className="text-gray-200 md:text-3xl lg:text-[50px] xl:text-[65px] whitespace-normal break-words mb-4 sm:mb-5 font-poppins tracking-[1px] md:leading-[1.1] lg:leading-[78px]">
                                                    {step.text1 ? (
                                                        <>
                                                            <span className="block font-thin">{step.text}</span>
                                                            <span className="block font-black">{step.text1}</span>
                                                        </>
                                                    ) : (
                                                        <span className="font-black">{step.text}</span>
                                                    )}
                                                </h3>
                                                <p className="text-gray-400 md:text-lg lg:text-xl xl:text-2xl font-poppins md:leading-[1.5] lg:leading-[33px]">
                                                    {step.description}
                                                </p>
                                            </div>
                                        ) : (
                                            /* Image */
                                            <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-lg   transition-all duration-500 ">
                                                <img src={step.image} alt={step.text} className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                    </div>

                                </div>
                            );
                        })}
                    </div>

                </div>
            </div>
        </section>
    );
};

export default TravelerFlowSection;